import { useAuthContext } from '@/contexts/AuthContext';
import { rtdb } from '@/Firebase';
import {
  CursorPosition,
  PresenceUser,
  UsePresenceProps,
  UsePresenceReturn,
} from '@/types/presence';
import {
  onDisconnect,
  onValue,
  ref,
  remove,
  serverTimestamp,
  set,
} from 'firebase/database';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Predefined colors for user cursors
const CURSOR_COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#FFA07A', // Salmon
  '#98D8C8', // Mint
  '#F7DC6F', // Yellow
  '#BB8FCE', // Purple
  '#85C1E2', // Sky Blue
  '#F8B88B', // Peach
  '#ABEBC6', // Green
];

function getColorForUser(userId: string): string {
  // Hash the userId to get a consistent color
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return CURSOR_COLORS[Math.abs(hash) % CURSOR_COLORS.length];
}

export function usePresence(props: UsePresenceProps): UsePresenceReturn {
  const { firebaseUser } = useAuthContext();
  const [users, setUsers] = useState<PresenceUser[]>([]);
  const [cursors, setCursors] = useState<Record<string, CursorPosition>>({});
  const [isActive, setIsActive] = useState(false);

  const presenceEstablished = useRef(false);
  const lastCursorUpdate = useRef<number>(0);

  // Extract the identifier and location based on mode
  const { accountId } = props;
  const identifier =
    props.mode === 'page' ? props.dynamicPageId : props.websiteId;
  const location = props.mode;

  // Throttle cursor updates to every 100ms
  const CURSOR_THROTTLE_MS = 100;

  const userPresenceRef = useMemo(() => {
    if (!firebaseUser || !accountId || !identifier) return null;
    return ref(
      rtdb,
      `presence/${accountId}/${identifier}/users/${firebaseUser.uid}`,
    );
  }, [accountId, identifier, firebaseUser]);

  const userCursorRef = useMemo(() => {
    if (location !== 'editor') return null;
    if (!firebaseUser || !accountId || !identifier) return null;
    return ref(
      rtdb,
      `presence/${accountId}/${identifier}/cursors/${firebaseUser.uid}`,
    );
  }, [accountId, identifier, firebaseUser, location]);

  // Establish presence
  useEffect(() => {
    if (!userPresenceRef || !firebaseUser) return;
    if (!accountId || !identifier) return;
    if (presenceEstablished.current) return;

    const buildPresenceData = () => {
      const data: any = {
        name: firebaseUser.displayName
          ? firebaseUser.displayName
          : firebaseUser.email || 'Anonymous',
        email: firebaseUser.email || '',
        location,
        lastSeen: serverTimestamp() as any,
      };
      if (firebaseUser.photoURL) {
        data.avatar = firebaseUser.photoURL;
      }
      return data;
    };

    // Set presence
    set(userPresenceRef, buildPresenceData())
      .then(() => {
        console.log('[Presence] Established presence');
        setIsActive(true);
        presenceEstablished.current = true;

        // Set up auto-removal on disconnect
        onDisconnect(userPresenceRef).remove();
        if (userCursorRef) {
          onDisconnect(userCursorRef).remove();
        }
      })
      .catch((error) => {
        console.error('[Presence] Failed to establish presence:', error);
      });

    // Update presence every 30 seconds as heartbeat (only when tab is visible)
    const heartbeatInterval = setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      console.log('[Presence] Sending heartbeat update', firebaseUser.uid);
      set(userPresenceRef, buildPresenceData()).catch((error) => {
        console.error('[Presence] Heartbeat failed:', error);
      });
    }, 30000);

    // Handle visibility changes - remove presence when tab is hidden
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Re-establish presence when tab becomes visible
        set(userPresenceRef, buildPresenceData())
          .then(() => {
            console.log('[Presence] Re-established presence on visibility');
            setIsActive(true);
          })
          .catch((error) => {
            console.error('[Presence] Failed to re-establish presence:', error);
          });
      } else {
        // Remove presence when tab is hidden
        console.log('[Presence] Removing presence - tab hidden');
        remove(userPresenceRef).catch((error) => {
          console.error('[Presence] Failed to remove presence on hide:', error);
        });
        if (userCursorRef) {
          remove(userCursorRef).catch((error) => {
            console.error('[Presence] Failed to remove cursor on hide:', error);
          });
        }
        setIsActive(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      clearInterval(heartbeatInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      presenceEstablished.current = false;
      setIsActive(false);

      // Remove presence
      if (userPresenceRef) {
        remove(userPresenceRef).catch((error) => {
          console.error('[Presence] Failed to remove presence:', error);
        });
      }
      if (userCursorRef) {
        remove(userCursorRef).catch((error) => {
          console.error('[Presence] Failed to remove cursor:', error);
        });
      }
    };
  }, [
    userPresenceRef,
    userCursorRef,
    firebaseUser,
    location,
    accountId,
    identifier,
  ]);

  // Listen to all users' presence
  useEffect(() => {
    if (!accountId || !identifier) return;

    const usersRef = ref(rtdb, `presence/${accountId}/${identifier}/users`);

    const unsubscribe = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setUsers([]);
        return;
      }

      const usersList: PresenceUser[] = Object.entries(data)
        .map(([userId, userData]: [string, any]) => ({
          userId,
          ...userData,
          color: getColorForUser(userId),
        }))
        .filter((u) => u.userId !== firebaseUser?.uid); // Exclude current user

      setUsers(usersList);
    });

    return () => {
      unsubscribe();
    };
  }, [accountId, identifier, firebaseUser?.uid]);

  // Listen to all users' cursors (only in editor mode)
  useEffect(() => {
    if (location !== 'editor' || !accountId || !identifier) return;

    const cursorsRef = ref(rtdb, `presence/${accountId}/${identifier}/cursors`);

    const unsubscribe = onValue(cursorsRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setCursors({});
        return;
      }

      // Filter out current user's cursor
      const cursorsData = { ...data };
      if (firebaseUser?.uid) {
        delete cursorsData[firebaseUser.uid];
      }

      setCursors(cursorsData);
    });

    return () => {
      unsubscribe();
    };
  }, [accountId, identifier, location, firebaseUser?.uid]);

  // Update cursor position (throttled) - only in editor mode and when others are present
  const updateCursor = useCallback(
    (x: number, y: number) => {
      if (location !== 'editor' || !userCursorRef) return;

      // Only send cursor updates if there are other users present
      if (users.length === 0) return;

      const now = Date.now();
      if (now - lastCursorUpdate.current < CURSOR_THROTTLE_MS) {
        return;
      }

      // console.log('[Presence] Updating cursor position', x, y);

      lastCursorUpdate.current = now;

      const cursorData: CursorPosition = {
        x,
        y,
        timestamp: now,
      };

      set(userCursorRef, cursorData).catch((error) => {
        console.error('[Presence] Failed to update cursor:', error);
      });
    },
    [userCursorRef, location, CURSOR_THROTTLE_MS, users.length],
  );

  // Merge cursors with user data - only in editor mode
  const cursorsWithUsers = useMemo(() => {
    if (location !== 'editor') return {};

    const result: Record<string, CursorPosition & { user: PresenceUser }> = {};

    Object.entries(cursors).forEach(([userId, cursor]) => {
      const user = users.find((u) => u.userId === userId);
      if (user) {
        result[userId] = { ...cursor, user };
      }
    });

    return result;
  }, [cursors, users, location]);

  return {
    users,
    cursors: cursorsWithUsers,
    updateCursor,
    isActive,
  };
}
